import ColoredChipsCell from "@common/components/coloredChipsCell/ColoredChipsCell";
import { TableCell } from "@mui/material";
import { Box } from "@mui/material";
import { DotLegalButton, DotLegalOverflowMenu, DotLegalPageHeader, DotLegalTableWithControls, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import TableRow from "@common/components/tableRow/TableRow";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { ExternalUserViewModel } from "../ExternalUser.types";
import { useExternalUsersHook } from "./ExternalUsers.hooks";
import AddEditExternalUserDialog from "../addEditExternalUserDialog/AddEditExternalUserDialog";
import { DeleteExternalUserDialog } from "../deleteExternalUserDialog/DeleteExternalUserDialog";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function ExternalUsers() {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const {
        isLoading,
        externalUsers,
        refetch,
        showAddEditDialog,
        setShowAddEditDialog,
        showDeleteDialog,
        setShowDeleteDialog,
        selectedExternalUser,
        setSelectedExternalUser,
        getBreadCrumbs,
    } = useExternalUsersHook();

    const getMenuItems = (externalUser: ExternalUserViewModel) => {
        if (!permissions.canManageUsers) return [];

        return [
            {
                menuItem: translateString("edit"),
                onClick: () => {
                    setSelectedExternalUser(externalUser);
                    setShowAddEditDialog(true);
                },
            },
            {
                menuItem: translateString("delete"),
                onClick: () => {
                    setSelectedExternalUser(externalUser);
                    setShowDeleteDialog(true);
                },
            },
        ];
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs()}
                rightComponent={
                    permissions.canManageUsers ? (
                        <DotLegalButton
                            buttonType="primary"
                            size="medium"
                            onClick={() => {
                                setSelectedExternalUser(undefined);
                                setShowAddEditDialog(true);
                            }}
                        >
                            {translateString("createExternalUser")}
                        </DotLegalButton>
                    ) : undefined
                }
            />

            <TablePagePaper>
                <DotLegalTableWithControls
                    internalScroll
                    hasOverflowMenu
                    headers={getHeaders(translateString)}
                    getUserSpecificPageLength={() => 30}
                    hideRowsPerPage
                    defaultOrderBy={"fullName"}
                    defaultOrder={"asc"}
                    clickableRows={false}
                    isLoading={isLoading}
                    labelRowsPerPage={translateString("showEntites")}
                    labelSearch={translateString("search")}
                    noOptionsLabel={translateString("noOptions")}
                    paginationLabelOf={translateString("labelOf")}
                    emptyText={translateString("noData")}
                    data={externalUsers ?? []}
                    renderRow={(row: ExternalUserViewModel, i: number) => (
                        <TableRow key={i}>
                            <TableCell>{row.fullName}</TableCell>
                            <TableCell>{row.email}</TableCell>
                            <TableCell>
                                <ColoredChipsCell items={row.businessAreas} />
                            </TableCell>
                            <TableCell align={"right"}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TablePagePaper>

            {showDeleteDialog && selectedExternalUser && (
                <DeleteExternalUserDialog
                    selectedExternalUser={selectedExternalUser}
                    onCloseDialog={() => setShowDeleteDialog(false)}
                    onDeletion={async () => {
                        await refetch();
                    }}
                />
            )}

            {showAddEditDialog && (
                <AddEditExternalUserDialog
                    externalUser={selectedExternalUser}
                    onCloseDialog={() => setShowAddEditDialog(false)}
                    onSave={async () => {
                        setShowAddEditDialog(false);
                        await refetch();
                    }}
                />
            )}
        </Box>
    );

    function getHeaders(translateString: (key: string) => string) {
        let headers = Array<ITableHeader<ExternalUserViewModel>>();
        headers.push({ property: "fullName", text: translateString("name"), align: "left", showOnMobile: true });
        headers.push({ property: "email", text: translateString("email"), align: "left", showOnMobile: true });
        headers.push({ property: "businessAreas", text: translateString("businessAreas"), align: "left", showOnMobile: true });
        return headers;
    }
}

export default ExternalUsers;
